import type { GraphQLResult } from 'aws-amplify/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import type { GridViewPreferenceItem } from '@/services/gridView/gridView.types'
import {
  upsertGridViewPreferenceMutation,
  upsertTeamGridViewMutation,
} from '@/services/graphql/mutations'
import { gridViewKeys } from '@/services/queries/gridView.keys'

const MANAGE_SOURCE = 'manage'

export type GridViewWriteErrorCode =
  | 'Unauthorized'
  | 'BadRequest'
  | 'Conflict'
  | 'Unknown'

export class GridViewWriteError extends Error {
  constructor(readonly code: GridViewWriteErrorCode) {
    super(`Grid view write failed: ${code}`)
    this.name = 'GridViewWriteError'
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object')

const readGridViewWriteErrorCode = (error: unknown): GridViewWriteErrorCode => {
  const errorRecord = isRecord(error) ? error : null
  const graphQLErrors = errorRecord?.errors
  const firstGraphQLError =
    Array.isArray(graphQLErrors) && graphQLErrors.length > 0
      ? graphQLErrors[0]
      : error
  const graphQLErrorRecord = isRecord(firstGraphQLError)
    ? firstGraphQLError
    : null
  const extensions = isRecord(graphQLErrorRecord?.extensions)
    ? graphQLErrorRecord.extensions
    : null
  const rawCodes = [
    extensions?.errorType,
    extensions?.code,
    graphQLErrorRecord?.type,
    graphQLErrorRecord?.name,
    graphQLErrorRecord?.message,
    errorRecord?.message,
  ].filter((value): value is string => typeof value === 'string')

  if (rawCodes.length === 0) return 'Unknown'
  if (rawCodes.some((value) => value.includes('Unauthorized'))) {
    return 'Unauthorized'
  }
  if (rawCodes.some((value) => value.includes('ConditionalCheckFailed'))) {
    return 'Conflict'
  }
  if (
    rawCodes.some(
      (value) => value.includes('BadRequest') || value.includes('Validation'),
    )
  ) {
    return 'BadRequest'
  }
  return 'Unknown'
}

const executeGridViewGraphql = async <T>(
  operation: () => Promise<GraphQLResult<T>>,
): Promise<GraphQLResult<T>> => {
  try {
    const result = await operation()
    if (result.errors?.length) {
      throw new GridViewWriteError(
        readGridViewWriteErrorCode({ errors: result.errors }),
      )
    }
    return result
  } catch (error) {
    if (error instanceof GridViewWriteError) throw error
    throw new GridViewWriteError(readGridViewWriteErrorCode(error))
  }
}

export type GridViewPreferenceWriteResult = {
  status: 'APPLIED' | 'CONFLICT'
  revision?: number | null
  preference?: GridViewPreferenceItem | null
}

export type GridViewInput = {
  id: string
  organizationID?: string
  scopeKey?: string
  name?: string
  description?: string | null
  payload?: string
  payloadVersion?: string
  gridSchemaVersion?: string
  status?: string
  isDefault?: boolean
  createdByUserID?: string
  updatedByUserID?: string
}

export type GridViewPreferenceInput = {
  id: string
  userID: string
  organizationID: string
  scopeKey: string
  selectedViewRef?: string | null
  draftPayload?: string | null
  payloadVersion: string
  gridSchemaVersion: string
  expectedRevision?: number | null
}

export type GridViewDeleteInput = {
  id: string
  organizationID: string
  scopeKey: string
  updatedByUserID: string
}

export type GridViewTeamViewWriteInput = Pick<
  GridViewInput,
  | 'id'
  | 'organizationID'
  | 'scopeKey'
  | 'name'
  | 'description'
  | 'payload'
  | 'payloadVersion'
  | 'gridSchemaVersion'
  | 'isDefault'
> & {
  operation: 'CREATE' | 'UPDATE' | 'DELETE'
}

export type GridViewTeamViewWriteResult = {
  id: string
  createdAt?: string | null
  updatedAt?: string | null
}

const isServerRevision = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1

const toTeamGridViewMutationInput = (
  input: GridViewInput,
  operation: 'CREATE' | 'UPDATE' | 'DELETE',
): GridViewTeamViewWriteInput => ({
  id: input.id,
  operation,
  organizationID: input.organizationID,
  scopeKey: input.scopeKey,
  name: input.name,
  description: input.description,
  payload: input.payload,
  payloadVersion: input.payloadVersion,
  gridSchemaVersion: input.gridSchemaVersion,
  isDefault: input.isDefault,
})

const toUserStatePreferenceMutationInput = (
  input: GridViewPreferenceInput,
) => ({
  id: input.id,
  organizationID: input.organizationID,
  scopeKey: input.scopeKey,
  selectedViewRef: input.selectedViewRef ?? null,
  draftPayload: input.draftPayload ?? null,
  payloadVersion: input.payloadVersion,
  gridSchemaVersion: input.gridSchemaVersion,
  expectedRevision: input.expectedRevision ?? null,
})

const upsertTeamGridView = async (
  input: GridViewTeamViewWriteInput,
): Promise<GridViewTeamViewWriteResult> => {
  const client = getAmplifyClient()
  const { data } = await executeGridViewGraphql(
    () =>
      client.graphql({
        query: upsertTeamGridViewMutation,
        variables: { input },
      }) as Promise<
        GraphQLResult<{
          upsertTeamGridView: GridViewTeamViewWriteResult | null
        }>
      >,
  )
  if (!data?.upsertTeamGridView) {
    throw new GridViewWriteError('Unknown')
  }
  return data.upsertTeamGridView
}

const createGridView = (input: GridViewInput) =>
  upsertTeamGridView(toTeamGridViewMutationInput(input, 'CREATE'))

const updateGridView = (input: GridViewInput) =>
  upsertTeamGridView(
    toTeamGridViewMutationInput(
      input,
      input.status === 'deleted' ? 'DELETE' : 'UPDATE',
    ),
  )

const upsertGridViewPreference = async (
  input: GridViewPreferenceInput,
): Promise<GridViewPreferenceWriteResult> => {
  const client = getAmplifyClient()
  const { data } = await executeGridViewGraphql(
    () =>
      client.graphql({
        query: upsertGridViewPreferenceMutation,
        variables: { input: toUserStatePreferenceMutationInput(input) },
      }) as Promise<
        GraphQLResult<{
          upsertGridViewPreference: GridViewPreferenceWriteResult | null
        }>
      >,
  )
  const result = data?.upsertGridViewPreference
  if (!result) {
    throw new GridViewWriteError('Unknown')
  }
  if (result.status === 'APPLIED' && !isServerRevision(result.revision)) {
    throw new Error('Grid view preference response is missing its revision')
  }
  return result
}

const isGridViewPreferenceItem = (
  value: unknown,
): value is GridViewPreferenceItem =>
  Boolean(
    value &&
    typeof value === 'object' &&
    'id' in value &&
    typeof (value as { id?: unknown }).id === 'string',
  )

const buildGridViewPreferenceCacheItem = ({
  input,
  previous,
  timestamp,
  revision,
}: {
  input: GridViewPreferenceInput
  previous?: GridViewPreferenceItem | null
  timestamp: string
  revision: number
}): GridViewPreferenceItem => ({
  ...input,
  status: 'active',
  createdAt: previous?.createdAt ?? timestamp,
  updatedAt: timestamp,
  revision,
  source: MANAGE_SOURCE,
})

export const useCreateGridView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['createGridView'],
    mutationFn: createGridView,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: gridViewKeys.detail(data.id),
      })
    },
  })
}

export const useUpdateGridView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateGridView'],
    mutationFn: updateGridView,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: gridViewKeys.detail(variables.id),
      })
    },
  })
}

export const useDeleteGridView = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteGridView'],
    mutationFn: async (input: GridViewDeleteInput) => {
      return updateGridView({
        id: input.id,
        organizationID: input.organizationID,
        scopeKey: input.scopeKey,
        updatedByUserID: input.updatedByUserID,
        status: 'deleted',
      })
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: gridViewKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: gridViewKeys.detail(variables.id),
      })
      queryClient.invalidateQueries({ queryKey: gridViewKeys.preferences() })
    },
  })
}

export const useUpsertGridViewPreference = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['upsertGridViewPreference'],
    mutationFn: async ({
      input,
    }: {
      upsertType: 'create' | 'update'
      input: GridViewPreferenceInput
    }) => {
      return upsertGridViewPreference(input)
    },
    onSuccess: (data, variables) => {
      if (data.status !== 'APPLIED' || !isServerRevision(data.revision)) {
        return
      }
      const revision = data.revision
      const timestamp = data.preference?.updatedAt ?? new Date().toISOString()
      const nextPreference = (previous?: GridViewPreferenceItem | null) =>
        data.preference ??
        buildGridViewPreferenceCacheItem({
          input: variables.input,
          previous,
          timestamp,
          revision,
        })

      queryClient.setQueryData<GridViewPreferenceItem | null>(
        gridViewKeys.preference(variables.input.id),
        (previous) =>
          nextPreference(isGridViewPreferenceItem(previous) ? previous : null),
      )
      queryClient.setQueriesData(
        { queryKey: gridViewKeys.preferences() },
        (previous: unknown) => {
          if (
            isGridViewPreferenceItem(previous) &&
            previous.id === variables.input.id
          ) {
            return nextPreference(previous)
          }

          return previous
        },
      )
    },
  })
}
